import type { ChipTone } from "../../../ui/styles/chipStyles";
import type { ThreadDisplayStatus } from "./threadStatus";

/** How one status of module C shows on a Run's badge. */
export interface RunStateLabel {
  /** Its word: `threads.status.<word>.label`. */
  readonly word: ThreadDisplayStatus;
  readonly tone: ChipTone;
  /** Shown only until the viewer opened the Run since it changed (T3 Code's unseen completion); then nothing. */
  readonly unseenOnly?: boolean;
  /** A spinner beside the word, while work goes on. */
  readonly spinning?: boolean;
}

/**
 * The one table of Run state labels, as T3 Code labels its threads (Johannes, 4 Nov 23:33; the orchestrator, 23:36, from
 * T3 Code's source a21d7887, SidebarThreadRow.tsx ~709, Sidebar.logic.ts ~882): attention and activity only, nothing on a
 * quiet, seen row (`null`; the row shows its age, the connection its own dot). Changing the mapping is a change here alone;
 * the hover keeps module C's exact explanation, and "Wartet auf …" names another person's host.
 */
export const RUN_STATE_LABELS: Readonly<Record<ThreadDisplayStatus, RunStateLabel | null>> = {
  approval: { word: "approval", tone: "info" },
  input: { word: "input", tone: "info" },
  working: { word: "working", tone: "info", spinning: true },
  waiting: { word: "working", tone: "info" },
  failed: { word: "failed", tone: "danger" },
  done: { word: "done", tone: "success", unseenOnly: true },
  ready: null,
  closed: null,
  offline: null,
};

/** T3 Code's precedence, Approval > Input > Working > Failed > Woke > Done > none: whether this status's label shows over
 * a woken Run's "Woke". */
export function outranksWoke(status: ThreadDisplayStatus): boolean {
  const shown = RUN_STATE_LABELS[status];
  return shown !== null && shown.unseenOnly !== true;
}

/** The status a Run's row shows: Input for a Run module C calls ready while its review or result waits for this person,
 * as it halts there for them (the orchestrator, 5 Nov 08:49, on Johannes's 08:45 words); else module C's own. */
export function rowStatus(status: ThreadDisplayStatus, waitsForMe: boolean): ThreadDisplayStatus {
  return waitsForMe && status === "ready" ? "input" : status;
}
